import { useState } from 'react'

import AtSymbolIcon from '@heroicons/react/outline/AtSymbolIcon'
import ChatIcon from '@heroicons/react/outline/ChatIcon'
import CogIcon from '@heroicons/react/outline/CogIcon'
import UserIcon from '@heroicons/react/outline/UserIcon'
import ViewGridIcon from '@heroicons/react/outline/ViewGridIcon'
import { useHistory } from 'react-router'

import { MemberEmailStatus } from '@tribeplatform/gql-client/types'
import { PostModal } from '@tribeplatform/react-components/PostComposer'
import {
  useAuthMember,
  useAuthToken,
  useCollections,
  useMemberPosts,
} from '@tribeplatform/react-sdk/hooks'
import { Card } from '@tribeplatform/react-ui-kit/Card'

import { useThemePreviewContext } from '../Admin/Editor/ThemePreviewContext'
import { VerifyModal } from '../Auth/VerifyModal'
import { ProfileSettingsModal } from '../Settings/ProfileSettingsModal'
import { CreateSpaceFromTemplateModal as CreateSpaceModal } from '../Space/CreateSpace'
import { UserImportModal } from '../UserImport'
import { MemberOnboardingPlaceholder } from './MemberOnboardingPlaceholder'
import { MemberOnboardingStep } from './Step'

export const MemberOnboarding = () => {
  const {
    data: { network },
  } = useAuthToken()
  const editorContext = useThemePreviewContext()
  const invokingWithinEditor = !!editorContext
  const { data: member, isAdmin, isGuest } = useAuthMember()

  const { data: collections, isLoading: isCollectionLoading } = useCollections({
    fields: {
      space: {
        image: 'basic',
        banner: 'basic',
        authMemberProps: 'all',
      },
    },
    variables: {},
    useQueryOptions: {
      retry: 10,
      retryDelay: 1000,
      enabled: isAdmin,
    },
  })
  const {
    data: memberPosts,
    isLoading: isMemberPostLoading,
    refetch: refetchMemberPosts,
  } = useMemberPosts({
    variables: { limit: 0, memberId: member?.id },
    useInfiniteQueryOptions: {
      refetchOnMount: 'always',
      enabled: isAdmin && !!member?.id,
    },
    fields: 'basic',
  })

  const [profileModal, setProfileModal] = useState(false)
  const [postModal, setPostModal] = useState(false)
  const [verifyModal, setVerifyModal] = useState(false)
  const [spaceModal, setSpaceModal] = useState(false)
  const [inviteModal, setInviteModal] = useState(false)

  const { push } = useHistory()

  if (invokingWithinEditor) return <MemberOnboardingPlaceholder />
  if (isGuest || !isAdmin) return null
  if (isCollectionLoading || isMemberPostLoading) return null

  const steps = []

  if (isAdmin) {
    steps.push({
      order: 10,
      id: 'confirm-email',
      title: 'Confirm your email',
      description: 'Verify your identity.',
      icon: AtSymbolIcon,
      completed: member.emailStatus === MemberEmailStatus.verified,
      button: {
        cta: 'Confirm',
        completed: 'Confirmed',
        variant: 'primary',
        action: () => {
          setVerifyModal(true)
        },
      },
    })

    steps.push({
      order: 20,
      title: 'Upload your logo',
      id: 'upload-logo',
      description: 'Upload community logo and set access level.',
      icon: CogIcon,
      completed: network?.logoId || network?.faviconId,
      button: {
        cta: 'Setup',
        completed: 'Done',
        variant: 'outline',
        action: () => {
          push('/manage/settings/network')
        },
      },
    })

    steps.push({
      order: 30,
      title: 'Define structure',
      id: 'create-space',
      description: 'Create collections and spaces',
      icon: ViewGridIcon,
      completed:
        collections
          ?.map(collection => collection?.spaces?.nodes?.length || 0)
          ?.reduce((count, n) => count + n) > 1,
      button: {
        cta: 'Create space',
        completed: 'Created',
        variant: 'outline',
        action: () => {
          setSpaceModal(true)
        },
      },
    })
  }

  steps.push({
    order: 40,
    title: 'Update your profile',
    id: 'update-profile',
    description: 'Add your photo and tagline.',
    icon: UserIcon,
    completed: member?.profilePictureId || member?.tagline,
    button: {
      cta: 'Update',
      completed: 'Updated',
      action: () => {
        setProfileModal(true)
      },
    },
  })

  steps.push({
    order: 50,
    title: 'Create a post',
    id: 'create-post',
    description: 'Engage with the community.',
    icon: ChatIcon,
    completed: memberPosts?.pages?.[0].totalCount > 0,
    button: {
      cta: 'Create post',
      completed: 'Done',
      action: () => {
        setPostModal(true)
      },
    },
  })

  // steps.push({
  //   order: 60,
  //   title: 'Invite members',
  //   id: 'invite-members',
  //   description: 'Invite your teammates and members.',
  //   icon: ChatIcon,
  //   completed: network?.membersCount > 1,
  //   button: {
  //     cta: 'Invite',
  //     completed: 'Done',
  //     action: () => {
  //       setInviteModal(true)
  //     },
  //   },
  // })

  if (steps?.filter(step => !step.completed)?.length === 0) return null

  steps.sort((a, b) => {
    if (a.completed && !b.completed) return 1
    if (b.completed && !a.completed) return -1
    return a.order > b.order ? 1 : -1
  })

  return (
    <>
      <Card className="overflow-hidden">
        <Card.Header
          title={isAdmin ? 'Setup your community' : 'Complete your profile'}
        />
        <div className="pt-3 pb-6 relative w-full overflow-x-auto flex pr-6">
          {steps.map(step => (
            <MemberOnboardingStep step={step} key={step.title} />
          ))}
        </div>
      </Card>
      {profileModal && (
        <ProfileSettingsModal
          member={member}
          open={profileModal}
          onClose={() => setProfileModal(false)}
        />
      )}
      {postModal && (
        <PostModal
          onClose={() => {
            refetchMemberPosts()
            setPostModal(false)
          }}
        />
      )}
      {verifyModal && (
        <VerifyModal open={verifyModal} onClose={() => setVerifyModal(false)} />
      )}
      {spaceModal && (
        <CreateSpaceModal
          open={spaceModal}
          onClose={() => setSpaceModal(false)}
        />
      )}
      {inviteModal && (
        <UserImportModal
          open={inviteModal}
          onClose={() => {
            setInviteModal(false)
          }}
        />
      )}
    </>
  )
}

MemberOnboarding.displayName = 'Member Onboarding'
